import '../styles/index.css';

import CookieConsent, { ConsentProvider, useConsent } from '@/components/CookieConsent';
import { sendGTMEvent } from '@next/third-parties/google';
import MarketingScripts from '@/components/MarketingScripts';
import Head from 'next/head';
import SiteConfig from '../config';

// import type { AppProps } from 'next/app'
import { GlobalStyle } from '../styles/global.css.js';
import React from 'react';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import dynamic from 'next/dynamic';
import isPropValid from '@emotion/is-prop-valid';
import theme from '../styles/theme';
import { useRouter } from 'next/router';

const VisualEditing = dynamic(
  () => import('@sanity/visual-editing/next-pages-router').then((mod) => mod.VisualEditing),
  { ssr: false },
);

// The Presentation tool in the embedded Studio renders the site in an iframe;
// only there should the visual-editing bridge (overlays, studio comlink) load.
// Draft-mode state isn't readable client-side (httpOnly cookie), so being
// framed is the signal — outside the Studio the component has no peer and the
// bundle should not ship at all.
function useIsFramed() {
  const [isFramed, setIsFramed] = React.useState(false);
  React.useEffect(() => {
    try {
      setIsFramed(window.self !== window.top);
    } catch {
      setIsFramed(true);
    }
  }, []);
  return isFramed;
}

function shouldForwardProp(propName, target) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}

// The `consent default` command lives in _document so it is the first script on
// the page. Here we only push updates, which must re-fire whenever the visitor
// changes their choice.
function GoogleConsentMode() {
  const { level, canUseAnalytics, canUseMarketing } = useConsent();

  React.useEffect(() => {
    if (!level || typeof window.gtag !== 'function') return;

    const marketing = canUseMarketing ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      ad_storage: marketing,
      ad_user_data: marketing,
      ad_personalization: marketing,
      analytics_storage: canUseAnalytics ? 'granted' : 'denied',
    });
  }, [level, canUseAnalytics, canUseMarketing]);

  return null;
}

// GTM's own History Change trigger is unreliable with the Next.js router, so
// every view — the first one included — is announced explicitly. The GA4 tag in
// the container must be bound to this `pageview` event, and its own "send a page
// view on load" option must be OFF, or the first view is counted twice.
function PageviewTracker() {
  const router = useRouter();
  const { isHydrated } = useConsent();
  const sentInitial = React.useRef(false);

  // The first view must wait for the stored choice to be read, otherwise it is
  // sent while consent still defaults to denied — GA4 then treats it as a
  // cookieless ping and it never shows up as a session.
  React.useEffect(() => {
    if (!isHydrated || sentInitial.current) return;
    sentInitial.current = true;
    sendGTMEvent({ event: 'pageview', page_path: router.asPath });
  }, [isHydrated, router.asPath]);

  // Also gated on hydration: a navigation during that window would otherwise
  // report a view while consent is still defaulted to denied.
  React.useEffect(() => {
    if (!isHydrated) return;

    const handleRouteChange = (url) => {
      sendGTMEvent({ event: 'pageview', page_path: url });
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [isHydrated, router.events]);

  return null;
}

function App({ Component, pageProps }) {
  const router = useRouter();
  const isFramed = useIsFramed();

  // The embedded Sanity Studio renders its own full-screen UI and must not
  // inherit the marketing site's global styles, theme, consent, or analytics.
  // Match only /studio and its sub-routes — not unrelated paths like /studios.
  if (router.pathname === '/studio' || router.pathname.startsWith('/studio/')) {
    return <Component {...pageProps} />;
  }

  const marketingRoutePattern = /^\/(contact|services|ai-native-assessment)(\/|$)/;
  const enableMarketing = marketingRoutePattern.test(router.pathname);

  return (
    <>
      <Head>
        <title>{SiteConfig.title}</title>
        <meta name="description" content={SiteConfig.description} />
      </Head>
      <GlobalStyle />
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <ThemeProvider theme={theme}>
          <ConsentProvider>
            <Component {...pageProps} />
            {isFramed && <VisualEditing />}
            <GoogleConsentMode />
            <PageviewTracker />
            <MarketingScripts enableMarketing={enableMarketing} />
            <CookieConsent />
          </ConsentProvider>
        </ThemeProvider>
      </StyleSheetManager>
    </>
  );
}

export default App;
